import _ from 'lodash';
import {
    BUDGET_LINE_STATUS_ARBITRATED,
    BUDGET_LINE_STATUS_EMPTY,
    BUDGET_LINE_STATUS_TO_VALIDATE,
    BUDGET_LINE_STATUS_READY,
    BUDGET_LINE_STATUS_TODO,
    BUDGET_LINE_STATUS_VOTED,
    BUDGET_LINE_STATUS_OBSOLETE,
    BUDGET_LINE_STATUS_ARCHIVED,
} from 'routes/BudgetModule/config/constants';
import {
    BudgetPhases,
    MANTY_COLORS,
    InscriptionStatusCategory,
    Phase,
    STATUS_FIELD,
    FRONT_DISPLAYED_IN_PROGRESS_STATUS,
    FRONT_DISPLAYED_TO_VALIDATE_STATUS,
    FRONT_DISPLAYED_DONE_STATUS,
} from 'business/constants';
import { StatusesSettingsT } from 'routes/BudgetModule/config/types';
import { statusFormatter } from 'ui/AgGridReact/formatters';
import { normalizeString } from 'helpers/helpers';
import {
    InscriptionStatusFields,
    InscriptionT,
} from 'routes/BudgetModule/shared/entities/inscription';
import {
    getStatusFieldForPhase,
    isActiveBudgetPhase,
} from 'routes/BudgetModule/shared/phases/helpers';
import { BUDGET_LINE_STATUS_TO_ARBITRATE } from '../config/constants';
import { getClientBind } from 'store/APIClient';
import { getPhaseSettings } from '../shared/phases/phase';

export const getStatusesValuesForPhase = (
    phase: BudgetPhases,
    statusesSettings: StatusesSettingsT,
): string[] => Object.keys(statusesSettings[phase] ?? {}) as string[];

/** Returns all statuses from statusesSettings. */
export const getAllStatuses = (statusesSettings: StatusesSettingsT): string[] =>
    _(statusesSettings)
        .values()
        .flatMap((statusConfig) => Object.keys(statusConfig ?? {}) as string[])
        .value();

export const isInscriptionRejected = (
    inscription: FlowAnyObject,
    statusField: string,
    phase: BudgetPhases,
    statusesSettings: StatusesSettingsT,
): boolean => {
    if (!isActiveBudgetPhase(phase)) return false;
    const phaseStatusesSettings = statusesSettings[phase];
    if (phaseStatusesSettings === undefined) return false;
    return (
        phaseStatusesSettings[inscription[statusField]] ===
        InscriptionStatusCategory.REJECTED
    );
};

/**
 * For supplementary budget phase, we consider voted status as rejected
 * not to take the inscription's amount into account.
 */
export const isInscriptionRejectedForSupplementary = (
    inscription: InscriptionT,
    statusField: InscriptionStatusFields,
    phase: BudgetPhases,
    statusesSettings: StatusesSettingsT,
): boolean => {
    if (!isActiveBudgetPhase(phase)) return false;
    const phaseStatusesSettings = statusesSettings[phase];
    if (phaseStatusesSettings === undefined) return false;
    return [
        InscriptionStatusCategory.REJECTED,
        InscriptionStatusCategory.VOTED,
    ].includes(phaseStatusesSettings[inscription[statusField]]);
};

export const isInscriptionRejectedForVoted = (
    inscription: InscriptionT,
    statusField: InscriptionStatusFields,
    phase: BudgetPhases,
    statusesSettings: StatusesSettingsT,
): boolean => {
    if (!isActiveBudgetPhase(phase)) return false;
    const phaseStatusesSettings = statusesSettings[phase];
    if (phaseStatusesSettings === undefined) return false;
    return (
        phaseStatusesSettings[inscription[statusField]] !==
        InscriptionStatusCategory.VOTED
    );
};

export const getRejectedStatusesForPhase = (
    phase: BudgetPhases,
    statusesSettings: StatusesSettingsT,
): string[] => {
    const phaseStatusesSettings = statusesSettings[phase];
    if (phaseStatusesSettings === undefined) return [];

    const allPhaseStatuses = Object.keys(phaseStatusesSettings) as string[];
    return allPhaseStatuses.filter(
        (status) =>
            phaseStatusesSettings[status] ===
            InscriptionStatusCategory.REJECTED,
    );
};

export const doesPhaseHaveRejectedStatuses = (
    phase: BudgetPhases,
    statusesSettings: StatusesSettingsT,
) => getRejectedStatusesForPhase(phase, statusesSettings).length > 0;

/**
 * Returns true if the inscription has the given status in the given phase.
 */
export const inscriptionHasStatus = (
    inscription: InscriptionT,
    phase: BudgetPhases,
    statusesSettings: StatusesSettingsT,
    status: InscriptionStatusCategory,
): boolean => {
    const statusField = getStatusFieldForPhase(phase);
    const statusesMapping = statusesSettings[phase];
    if (statusesMapping === undefined) return false;

    if (statusField === undefined) {
        return false;
    }

    return (
        inscription[statusField] in statusesMapping &&
        statusesMapping[inscription[statusField!]] === status
    );
};

/**
 * Returns true if the inscription has the status voted in the given phase.
 * @returns True if voted, false otherwise.
 */
export const isInscriptionVoted = (
    inscription: InscriptionT,
    phase: BudgetPhases,
    statusesSettings: StatusesSettingsT,
): boolean =>
    inscriptionHasStatus(
        inscription,
        phase,
        statusesSettings,
        InscriptionStatusCategory.VOTED,
    );

const getAllStatusesMapping = (
    statusesSettings: StatusesSettingsT,
): { [key: string]: InscriptionStatusCategory } => {
    let allStatusesMapping = {};
    Object.values(statusesSettings).forEach(
        (statusesMapping) =>
            (allStatusesMapping = {
                ...allStatusesMapping,
                ...statusesMapping,
            }),
    );

    return allStatusesMapping;
};

export const isStatusVoted = (
    status: string,
    statusesSettings: StatusesSettingsT,
): boolean => {
    const allStatusesMapping = getAllStatusesMapping(statusesSettings);
    const allVotedStatuses = Object.keys(allStatusesMapping).filter(
        (s) => allStatusesMapping[s] === InscriptionStatusCategory.VOTED,
    );
    return allVotedStatuses.includes(status);
};

/** AggStatuses are the statuses held by the budget lines and operations
 * they are the result of inscription statuses' aggregations
 */
export const getAggStatusColor = (status: string): string => {
    switch (status) {
        case BUDGET_LINE_STATUS_TODO:
            return MANTY_COLORS.orange;
        case BUDGET_LINE_STATUS_TO_VALIDATE:
        case BUDGET_LINE_STATUS_TO_ARBITRATE:
            return MANTY_COLORS.yellow;
        case BUDGET_LINE_STATUS_OBSOLETE:
        case BUDGET_LINE_STATUS_EMPTY:
        case BUDGET_LINE_STATUS_ARCHIVED:
            return MANTY_COLORS.mediumGrey;
        case BUDGET_LINE_STATUS_READY:
        case BUDGET_LINE_STATUS_ARBITRATED:
        case BUDGET_LINE_STATUS_VOTED:
        default:
            return MANTY_COLORS.green;
    }
};

/** Get aggregated status of an inscription, depending on its phase */
export const getAggStatus = (
    inscriptions: InscriptionT[],
    phase: BudgetPhases,
    statusesSettings: StatusesSettingsT,
    phasesSettings: Phase[],
    hasBudgetLineForCurrentExercice: boolean,
    isAMPBudgetableImputation?: boolean,
): string => {
    const phaseStatusesSettings = statusesSettings[phase];
    const phaseSettings = getPhaseSettings(phasesSettings, phase);
    if (phaseStatusesSettings === undefined || phaseSettings === undefined)
        return BUDGET_LINE_STATUS_ARCHIVED;
    const statusField = phaseSettings[STATUS_FIELD];
    const inProgressStatus = phaseSettings[FRONT_DISPLAYED_IN_PROGRESS_STATUS];
    const toValidateStatus = phaseSettings[FRONT_DISPLAYED_TO_VALIDATE_STATUS];
    const doneStatus = phaseSettings[FRONT_DISPLAYED_DONE_STATUS];

    // The voted inscriptions are not taken into account
    const rows = inscriptions.filter(
        (inscription) =>
            !isInscriptionVoted(inscription, phase, statusesSettings),
    );
    const inscriptionsStatuses = rows.map((inscription) =>
        statusField !== ''
            ? phaseStatusesSettings[inscription[statusField]]
            : InscriptionStatusCategory.NULL,
    );
    if (inscriptionsStatuses.length === 0) {
        const votedRowsNumber = inscriptions.filter((inscription) =>
            isInscriptionVoted(inscription, phase, statusesSettings),
        ).length;
        if (votedRowsNumber === 0) {
            /**
             * Specific to AMP, the operation segment is budgetable if it's a "mother" operation, meaning it doesn't have a "parent_software_id"
             * Other Sedit Finances customers may have the same principle for other fields like nature, so we'll need to refactor this when we'll
             * be more confident on the right way to do it
             */
            if (getClientBind() === 'ampmetropole') {
                return isAMPBudgetableImputation &&
                    hasBudgetLineForCurrentExercice
                    ? BUDGET_LINE_STATUS_EMPTY
                    : BUDGET_LINE_STATUS_OBSOLETE;
            }
            return hasBudgetLineForCurrentExercice
                ? BUDGET_LINE_STATUS_EMPTY
                : BUDGET_LINE_STATUS_OBSOLETE;
        }
        return BUDGET_LINE_STATUS_VOTED;
    }
    if (
        inscriptionsStatuses.some(
            (e) => e === InscriptionStatusCategory.IN_PROGRESS,
        )
    ) {
        return inProgressStatus;
    }
    if (
        inscriptionsStatuses.some(
            (e) => e === InscriptionStatusCategory.TO_VALIDATE,
        )
    ) {
        return toValidateStatus;
    }
    if (
        inscriptionsStatuses.every(
            (e) =>
                e === InscriptionStatusCategory.ACCEPTED ||
                e === InscriptionStatusCategory.REJECTED,
        )
    ) {
        return doneStatus;
    }

    return inProgressStatus;
};

/**
 * Returns InscriptionStatusCategory associated to given status
 * or INITIAL category in case of no matching category (empty, archived)
 */
const getInscriptionStatusCategory = (
    status: string,
    statusesSettings: StatusesSettingsT,
): InscriptionStatusCategory => {
    const statusesMapping = getAllStatusesMapping(statusesSettings);
    return status in statusesMapping
        ? statusesMapping[status]
        : InscriptionStatusCategory.NULL;
};

export const getStatusColor = (
    status: string,
    statusesSettings: StatusesSettingsT,
): string => {
    const statusCategory = getInscriptionStatusCategory(
        status,
        statusesSettings,
    );
    switch (statusCategory) {
        case InscriptionStatusCategory.NULL:
            return MANTY_COLORS.mediumGrey;
        case InscriptionStatusCategory.REJECTED:
            return MANTY_COLORS.red;
        case InscriptionStatusCategory.IN_PROGRESS:
            return MANTY_COLORS.orange;
        case InscriptionStatusCategory.TO_VALIDATE:
            return MANTY_COLORS.yellow;
        default:
            return MANTY_COLORS.green;
    }
};

export const statusComparator = (valueA: string, valueB: string): number => {
    let valueAFormatted = statusFormatter({ value: valueA });
    let valueBFormatted = statusFormatter({ value: valueB });
    if (valueAFormatted && !valueBFormatted) {
        return 1;
    }
    if (!valueAFormatted && valueBFormatted) {
        return -1;
    }

    if (!valueAFormatted || !valueBFormatted) {
        return 0;
    }

    // Since we made sure valueA and valueB where not null, we can normalize the string
    valueAFormatted = normalizeString(valueAFormatted);
    valueBFormatted = normalizeString(valueBFormatted);
    if (valueAFormatted < valueBFormatted) {
        return -1;
    }
    if (valueAFormatted > valueBFormatted) {
        return 1;
    }
    return 0;
};
